import { h, Fragment, Component, Prop, Element, Event, Watch } from '@stencil/core';
import type { ComponentInterface, EventEmitter } from '@stencil/core';
import { pointerHover } from 'utils';

@Component({
	tag: 'app-details',
	styleUrl: 'details.scss',
	shadow: true
})
export class AppDetails implements ComponentInterface {
	@Element() root: HTMLAppDetailsElement;

	/** Disables the toggle button */
	@Prop({ reflect: true }) disabled = false;
	/** Whether the details are open */
	@Prop({ mutable: true, reflect: true }) open = false;
	/** Name of the mutually-exclusive details group (accordion behavior) */
	@Prop({ reflect: true }) name?: string;
	/**
	 * If hovering is supported by the medium:
	 * - optionally visually hide the toggle button unless it is `:focus-visible` with `.hide-toggle`
	 * - details will be open during `:hover`
	 */
	@Prop({ reflect: true }) interestToggle = false;
	/**
	 * Shows the summary and the details while hiding the toggle and ignoring openness.
	 */
	@Prop({ reflect: true }) forceShow = false;

	@Prop() cssHide = false;

	@Prop() navParent = false;

	openedBy: undefined | 'interest-hover' | 'explicit-toggle';

	@Event() appDetailsToggled: EventEmitter<boolean>;
	@Watch('open')
	onOpenChange() {
		if (!this.open)
			this.openedBy = undefined;
		this.appDetailsToggled.emit(this.open);
		this.toggleLogin();
	}

	componentWillLoad() {
		addEventListener('appDetailsToggled', (e: CustomEvent<boolean>) => {
			const other = e.composedPath()[0] as HTMLAppDetailsElement;
			if (other !== this.root && this.name && other.name === this.name && other.open)
				this.open = false;
		});

		addEventListener('keydown', ({ key }) => {
			if (key === 'Escape')
				this.open = false;
		});

		pointerHover(this.root, {
			onlyTrueHovers: true,
			onEachEnter: () => {
				if (!this.interestToggle)
					return false;

				const olb = document.querySelector('app-menu > [slot=button] .olb');
				if(olb && olb.classList.contains('active')) {
					this.open = false;
					return false;
				}
				

				if (!this.open)
					this.openedBy = 'interest-hover';
				this.open = true;
			},
			onAllLeave: () => {
				if (this.openedBy === 'interest-hover')
					this.open = false;
			}
		});
		this.updateMulticolumnGrid();
	}

	updateMulticolumnGrid(): void {
		const multicolumns = document.querySelectorAll(
		  'app-menu > [slot=menu] > div app-details[name=menu-level-1] .multicolumn'
		);
	  
		multicolumns.forEach((multicolumn) => {
			const lis = multicolumn.querySelectorAll(':scope > li');
			const hasNoDirectChildAppDetails = Array.from(lis).every(
				(li) => !li.querySelector(':scope > app-details')
			);
		
			if (hasNoDirectChildAppDetails) {
				const parentAppDetails = multicolumn.closest('app-details');
				if(parentAppDetails) {
					parentAppDetails.classList.add('single-column');
				}
			} else {
				(multicolumn as HTMLElement).classList.remove('single-column');
			}
		});
	  }

	  toggleLogin() {
		const olb = document.querySelector('app-menu > [slot=button] .olb');
		const appDetails = this.root.open;

		if(olb && olb.classList.contains('active') && appDetails) {
			olb.classList.remove('active');
		}
	  }

	render() {
		return <>
			<div id='summary' part='summary'>
				<slot name='summary' />

				<button
					id='toggle'
					part='toggle'
					type='button'
					aria-controls='content'
					aria-expanded={this.open + ''}
					onClick={() => {
						if (!this.open)
							this.openedBy = 'explicit-toggle';
						this.open = !this.open;
					}}
					disabled={this.disabled}
					hidden={this.forceShow}
				>
					<slot name='toggle'>
						<slot name='toggle-label'>
							<slot name='open-label'>
								<span class='visually-hidden'>Open</span>
							</slot>
							<slot name='close-label'>
								<span class='visually-hidden'>Close</span>
							</slot>
						</slot>

						<slot name='toggle-icon'>
							<slot name='open-icon'>
								<app-icon part='toggle-icon open' icon='angle-up' />
							</slot>
						</slot>
					</slot>
				</button>
			</div>

			<div
				id='content'
				part='content'
				hidden={!this.cssHide && !this.open && !this.forceShow}
				class={this.cssHide && this.open ? 'content-open' : 'content-closed'}>

				{this.navParent && (
					<div class="close-nav-parent" onClick={() => {this.open = false}}>
						<app-icon icon="arrow-right-alt"></app-icon>
					</div>
				)}

				<slot />
			</div>
		</>;
	}
}
